<template>
  <el-dialog
    id="invoiceModal"
    :visible.sync="showLocal"
    :width="dialogWidth"
  >
    <div v-loading="processLoading">
      <template v-if="invoice">
        <!-- Header -->
        <div
          class="header"
          :class="['mobile', 'tablet'].includes($mq) ? 'mobileMode' : 'deskMode'"
        >
          <!-- Left -->
          <div class="left">
            <!-- Coach name -->
            <span class="headerLine invoiceInfo">
              {{ $t('coach.invoice.detail.invoiceDate') }}:
              <b>{{ $moment(invoice.invoice_date).format('YYYY-MM-DD') }}</b>
            </span>

            <span class="headerLine invoiceInfo">
              {{ $t('coach.invoice.detail.ceNumber') }}:
              <b>{{ invoice.id }}</b>
            </span>

            <!-- Optional id -->
            <div class="optionalId">
              {{ $t('coach.invoice.detail.optionalNumber') }}:
              <div v-if="optionalEdit">
                <el-input
                  v-model="invoice.optional_id"
                  class="optionalInput"
                  :disabled="!optionalEdit"
                />
              </div>
              <div v-else>
                <span
                  v-if="invoice.optional_id"
                  class="optionalInput static"
                >
                  {{ invoice.optional_id }}
                </span>
              </div>

              <el-button
                v-if="!optionalEdit"
                class="optionalEditButton"
                @click="editOptionalId"
              >
                {{ $t('common.edit') }}<i class="el-icon-edit"></i>
              </el-button>

              <el-button
                v-else
                class="optionalEditButton"
                @click="editOptionalId"
              >
                {{ $t('common.save') }}<i class="el-icon-edit"></i>
              </el-button>
            </div>
          </div>
        </div>

        <div class="content">
          <!-- Table leader list -->
          <el-table
            ref="invoiceModalTable"
            :data="invoice.engagements"
            stripe
            class="clear-table invoiceModalTable"
            empty-text="No Invoices created yet."
          >
            <el-table-column
              prop="short_id"
              :label="$t('coach.invoice.detail.engagementId')"
              :min-width="150"
            />
            <el-table-column
              prop="invoice_ct"
              :label="$t('common.count')"
              :min-width="80"
            />
            <el-table-column
              prop="leader.first_name"
              :label="$t('coach.invoice.detail.leaderName')"
              :min-width="150"
            >
              <template slot-scope="name">
                {{ name.row.leader.first_name }}
                {{ name.row.leader.last_name }}
              </template>
            </el-table-column>
            <el-table-column
              prop="amount"
              :label="`${$t('coach.invoice.detail.amount')} (${currency})`"
              :min-width="150"
              align="right"
            >
              <template slot-scope="amount">
                {{ currencySymbol }} {{ amount.row.amount }}
              </template>
            </el-table-column>
          </el-table>

          <!-- Table footer - Amount -->
          <div
            class="tableFooter"
            :class="['tablet', 'mobile'].includes($mq) ? 'mobileMode' : 'deskMode'"
          >
            <div class="footerRow">
              <div class="footerCol">
                <div class="left">
                  {{ $t('coach.invoice.detail.subtotal') }}
                </div>
                <div class="right">
                  <span class="dolorSymbol">
                    {{ currencySymbol }}
                  </span>
                  {{ invoice.sub_total }}
                </div>
              </div>
            </div>
            <div class="footerRow">
              <div class="footerCol">
                <div class="left">
                  {{ $t('coach.invoice.detail.discount') }}
                </div>
                <div class="right discountRight">
                  <span class="dolorSymbol">
                    - {{ currencySymbol }}
                  </span>

                  <div>
                    <span
                      class="optionalInput static"
                    >
                      {{ invoice.discount }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div class="footerRow">
              <div class="footerCol">
                <div class="left">
                  {{ $t('coach.invoice.detail.vat') }}
                </div>
                <div class="right">
                  {{ invoice.vat ? `${invoice.vat}%` : 'None' }}
                </div>
              </div>
            </div>
            <div class="footerRow grandTotal">
              <div class="footerCol">
                <div class="left">
                  {{ $t('coach.invoice.detail.totalAmount') }}
                </div>
                <div class="right">
                  <span class="dolorSymbol">
                    {{ currencySymbol }}
                  </span>
                  {{ invoice.grand_total }}
                </div>
              </div>
            </div>
          </div>

          <!-- Button Wrap. -->
          <div class="buttonWrap">
            <el-button
              type="default"
              class="closeBtn"
              round
              @click="showLocal = false"
            >
              {{ $t('common.close') }}
            </el-button>
            <el-button
              type="primary"
              class="viewBtn"
              round
              @click="viewInvoice(invoice.id)"
            >
              {{ $t('coach.invoice.detail.viewInvoice') }}
            </el-button>
          </div>
        </div>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import DialogSetting from '@/mixins/dialog';
import api from '@/api';

export default {
  mixins: [DialogSetting],

  props: {
    show: {
      type: Boolean,
      default: false,
    },

    modalType: {
      type: String,
      default: null,
    },

    selectedId: {
      type: String,
      default: null,
    },

    role: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      processLoading: false,
      statusList: [
        {
          label: this.$t('common.draft'),
          value: 'draft',
        },
        {
          label: this.$t('coach.invoice.detail.paymentSent'),
          value: 'payment_sent',
        },
        {
          label: this.$t('coach.invoice.detail.paymentPending'),
          value: 'pending',
        },
        {
          label: this.$t('coach.invoice.detail.paymentCompleted'),
          value: 'completed',
        },
      ],
      status: '',
      optionalEdit: false,
      paymentEdit: false,
      noteEdit: false,
      discountEdit: false,
      paymentNote: null,
      invoice: null,
    };
  },

  computed: {
    currency() {
      return process.env.VUE_APP_CURRENCY_SHORT || 'USD';
    },

    currencySymbol() {
      return process.env.VUE_APP_CURRENCY_SYMBOL || '$';
    },
  },

  watch: {
    show(newValue) {
      if (newValue) {
        this.getData();
      } else {
        this.invoice = null;
      }
    },
  },

  methods: {
    async getData() {
      try {
        this.processLoading = true;
        const response = await api.getOneCoachInvoice(this.selectedId);
        this.invoice = response.data;
      } catch (err) {
        this.showLocal = false;
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.processLoading = false;
      }
    },

    async editOptionalId() {
      if (!this.optionalEdit) {
        this.optionalEdit = !this.optionalEdit;
      } else {
        this.patchInvoice('id');
      }
    },

    async editPaymentDate() {
      if (!this.paymentEdit) {
        this.paymentEdit = !this.paymentEdit;
      } else {
        this.patchInvoice('date');
      }
    },

    async patchInvoice(type) {
      try {
        this.processLoading = true;
        let object;
        if (type === 'id') {
          object = {
            optional_id: this.invoice.optional_id,
          };
        } else if (type === 'date') {
          object = {
            payment_date: this.invoice.payment_date,
          };
        } else if (type === 'discount') {
          object = {
            discount: this.invoice.discount,
          };
        }

        await api.patchCoachInvoice(this.selectedId, object);

        if (type === 'id') {
          this.$message.success(this.$t('coach.invoice.detail.numberSaved'));
        } else {
          this.$message.success(this.$t('coach.invoice.detail.dateSaved'));
        }

        if (type === 'id') {
          this.optionalEdit = false;
        } else if (type === 'date') {
          this.paymentEdit = false;
        } else if (type === 'discount') {
          this.discountEdit = false;
        }
      } catch (err) {
        console.log(err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      } finally {
        this.processLoading = false;
      }
    },

    async editStatus(status) {
      this.$confirm(`${this.$t('coach.invoice.detail.askStatusChange')}`, '', {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.goBack'),
        roundButton: true,
        customClass: 'statusConfirmation',
      }).then(async () => {
        try {
          await api.patchMAMCoachInvoiceOut(this.selectedId, { status });
          this.$message({
            message: `${this.$t('coach.invoice.detail.statusChanged')}`,
            type: 'success',
          });
        } catch (err) {
          if (err.msg) {
            this.$message.error(err.msg);
          }
        }
      }).catch(() => {
        this.leader.sub_group_uuid = this.teamBackup;
      });
    },

    async editNote() {
      if (!this.noteEdit) {
        this.noteEdit = true;
      } else {
        try {
          await api.patchMAMCoachInvoiceOut(this.selectedId, { notes: this.invoice.notes });
          this.$message({
            message: `${this.$t('coach.invoice.detail.noteChanged')}`,
            type: 'success',
          });
        } catch (err) {
          if (err.msg) {
            this.$message.error(err.msg);
          }
        } finally {
          this.noteEdit = false;
        }
      }
    },

    editDiscount() {
      if (!this.discountEdit) {
        this.discountEdit = !this.discountEdit;
      } else {
        this.patchInvoice('discount');
      }
    },

    viewInvoice(id) {
      const routeData = this.$router.resolve({ name: 'invoiceDetail', params: { id } });
      window.open(routeData.href, '_blank');
    },
  },
};
</script>

<style lang="scss" scoped>
  #invoiceModal {
    .header {
      display: flex;
      margin-bottom: 20px;

      .left {
        width: 70%;

        .invoiceInfo {
          display: flex;
          flex-wrap: wrap;
          height: 30px;
          align-items: center;
        }

        .coachName {
          width: 100%;
        }

        .headerLine {
          height: 30px;
          margin: 0 10px 0 0;
          display: flex;
          align-items: center;
        }

        .optionalId {
          display: flex;
          flex-wrap: wrap;
          align-items: center;

          .optionalInput {
            &.static {
              padding-right: 5px;
            }
          }

          .optionalEditButton {
            padding: 7px 10px;
            // border-color: rgba(100, 100, 100, 0);
          }

          .optionalInput {
            width: 200px;
            // margin: 0 3px;
            height: 30px;

            &.static {
              // padding: 0 15px;
              // border-bottom: 1px solid #818181;
            }
          }
        }

        .paymentDate {
          display: flex;
          flex-wrap: wrap;
          align-items: center;

          .paymentEditButton {
            padding: 7px 10px;
            border-color: rgba(100, 100, 100, 0);
          }

          .paymentInput {
            width: 200px;
            // margin: 0 3px;
            height: 30px;

            &.static {
              // padding: 0 15px;
              // border-bottom: 1px solid #818181;
            }
          }
        }
      }

      .right {
        width: 30%;
        text-align: right;

        .el-select {
          width: 100%;
          max-width: 200px;
        }
      }

      &.mobileMode {
        flex-wrap: wrap;

        .left,
        .right {
          width: 100%;

          .el-select {
            margin-top: 15px;
            max-width: 900px;
          }
        }
      }
    }

    .content {
      .invoiceModalTable {
        width: 100%;
        border: 1px solid #212121;
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
        color: black;
      }

      .tableFooter {
        .footerRow {
          min-height: 48px;
          border-left: 1px solid #212121;
          border-right: 1px solid #212121;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: flex-end;
          margin-left: auto;
          padding: 5px 15px;
          box-sizing: border-box;
          font-size: 18px;

          .footerCol {
            width: 56%;
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            align-items: center;
          }

          &:last-child {
            border: 1px solid #212121;
            border-bottom-right-radius: 10px;
            border-bottom-left-radius: 10px;
          }

          &.grandTotal {
            font-weight: bold;
          }

          .right {
            align-items: center;

            &.discountRight {
              display: flex;

              .discountEditButton {
                padding: 4px 8px;
                margin: 0 0 0 5px;
                border: none;
              }

              .el-input {
                width: 100px;
                height: 30px;
              }
            }

            .dolorSymbol {
              margin-right: 5px;
            }
          }
        }

        &.mobileMode {
          .footerCol {
            width: 100%;
            max-width: 285px;
          }
        }
      }

      .paymentNoteWrap {
        margin-top: 20px;

        .paymentHeader {
          padding: 0 0 15px;

          .title {
            font-weight: bold;
          }

          .editBtn {
            margin-left: 10px;
          }
        }
      }

      .buttonWrap {
        display: flex;
        justify-content: center;
        padding: 30px 0 30px 0;

        .el-button {
          width: 50%;
          max-width: 160px;
          padding: 12px 0;

          &.closeBtn {
            border-color: #3f9eff;
            color: #3f9eff;
          }
        }
      }
    }
  }
</style>

<style lang="scss">
  #invoiceModal {
    .el-dialog__body {
      padding: 30px;
      color: black;
      white-space: pre-wrap;
    }

    .header {
      .left {
        .optionalInput {
          .el-input__inner {
            height: 100%;
          }

          &.is-disabled {
            .el-input__inner {
              background-color: rgba(255, 255, 255, 0);
              border-bottom: 1px solid rgba(100, 100, 100, 1);
              border-left: 1px solid rgba(100, 100, 100, 0);
              border-right: 1px solid rgba(100, 100, 100, 0);
              border-top: 1px solid rgba(100, 100, 100, 0);
              border-radius: 0;
            }
          }
        }
      }

      .right {
        .el-select {
          .el-input__inner:not(:focus) {
            border-color: #212121;
          }

          .el-input__icon {
            color: black;
          }
        }
      }
    }

    .content {
      .invoiceModalTable {
        thead {
          color: black;

          th {
            border-color: #212121;
          }
        }

        tr {
          th:first-child {
            padding-left: 10px;
          }

          td:first-child {
            padding-left: 10px;
          }
        }
      }

      .footerRow {
        .footerCol {
          .discountInput {
            height: 30px;

            .el-input__inner {
              height: 100%;
            }
          }
        }
      }

      .paymentNoteWrap {
        .paymentNote {
          .el-textarea__inner:not(:focus) {
            border-color: #212121;
          }
        }
      }
    }
  }
</style>
